import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, catchError, groupBy, switchMap } from 'rxjs/operators';
import * as DataActions from './data.actions';
import { ComponentDataSourceService } from '../../services/component-data-source/component-data-source.service';
import { FacetFilterConfig, mapFacetsToTableFilters } from '../../interfaces/DataSource/FacetFilterConfig.interface';

@Injectable()
export class DataEffects {
  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataActions.loadData),
      groupBy(({ dataSourceKey }) => dataSourceKey),
      mergeMap(group =>
        group.pipe(
          switchMap(({ dataSourceKey, searchQuery, filters, sort }) => {
            const key = dataSourceKey ?? '';
            if (!key) return of();
            return this.dataSourceService.loadDataWithFilters(key, { dataSourceKey: key, searchQuery, filters, sort }).pipe(
              map(data => {
                const baseSettings =
                  data?.settings ?? this.dataSourceService.getDataSourceSettings(key);
                const facetConfig = baseSettings?.facetFilters as FacetFilterConfig | undefined;
                let settings = baseSettings;

                if (facetConfig?.fields?.length) {
                  const facetFilters = mapFacetsToTableFilters(
                    data?.facets ?? {},
                    facetConfig,
                    data?.filters ?? baseSettings?.filters
                  );
                  settings = {
                    ...baseSettings,
                    filters: facetFilters,
                  };
                } else if (baseSettings?.filters?.length && filters?.length) {
                  const valueByColumn = new Map<string, unknown>();
                  for (const filter of filters) {
                    if (filter.value != null && filter.value !== '') {
                      valueByColumn.set(filter.column, filter.value);
                    }
                  }
                  settings = {
                    ...baseSettings,
                    filters: baseSettings.filters.map((filter: { column: string; value: unknown }) => ({
                      ...filter,
                      value: valueByColumn.has(filter.column)
                        ? valueByColumn.get(filter.column)
                        : filter.value,
                    })),
                  };
                }

                return DataActions.loadDataSuccess({
                  dataSourceKey: key,
                  data,
                  settings,
                });
              }),
              catchError(error => {
                console.error(`[DataEffects] Error loading data for ${key}:`, error);
                return of(DataActions.loadDataFailure({ dataSourceKey: key, error }));
              })
            );
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private dataSourceService: ComponentDataSourceService
  ) {}
} 